import { loggerUtil } from '@cmg/common';

import usePollingWhenVisible from '../../../../../../../common/hooks/usePollingWhenVisible';
import { isValidCmgEntityKey } from '../../../../../../../common/util/isValidCmgEntityKey';
import { canSyndicateManagerUpdateInstitutionalIndication } from '../../../../../../../common/util/offering/syndicate-entitlements';
import { OrderBookPersonaType } from '../../../../hooks/useIdentifyOrderBookPersona';
import { useSyndicateIndicationQuery } from '../../../context/hooks/syndicate/__generated__';
import {
  OfferingSidePanelModel,
  offeringSidePanelModelDefaults,
  PersonaProps,
} from '../useOfferingSidePanelModel';

export type Props = PersonaProps & {
  cmgEntityKey?: string | null;
  indicationId?: string | null;
};

export const useSyndicateIndicationModel = ({
  offeringId,
  cmgEntityKey,
  type,
  indicationId,
}: Props): OfferingSidePanelModel => {
  const {
    loading,
    data: indicationData,
    error,
    stopPolling,
    startPolling,
    called,
  } = useSyndicateIndicationQuery({
    variables: {
      offeringId,
      // cmgEntityKey context being actually firmKey means investor not paired to any cmg entity
      cmgEntityKey: isValidCmgEntityKey(cmgEntityKey!) ? cmgEntityKey : null,
      indicationId,
    },
    skip: !cmgEntityKey || type !== OrderBookPersonaType.SYNDICATE || !offeringId,
    fetchPolicy: 'cache-and-network',
  });

  usePollingWhenVisible({ pollInterval: 5_000, startPolling, stopPolling, called });

  if (error) {
    loggerUtil.error(new Error('Error in useSyndicateIndicationModel'), {
      error,
      offeringId,
      cmgEntityKey,
      indicationId,
      type,
      url: window.location.href,
    });
  }

  const offering = indicationData?.basicPublishedOffering!;
  const orderBook = offering?.orderBook;
  const institutionalIndication = orderBook?.institutionalIndication;

  return {
    ...offeringSidePanelModelDefaults,
    indication: {
      data: institutionalIndication,
      canRead: true,
      canUpdate: true,
    },
    allocationAcknowledgment: {
      canRead: true,
      canUpdate: true,
      canBillingAndDeliveryAgentUpdate: canSyndicateManagerUpdateInstitutionalIndication({
        cmgEntityKey: institutionalIndication?.billingAndDeliveryAgentCmgEntityKey,
        syndicatesEntitlements:
          indicationData?.publishedOfferings?.items?.[0]?.syndicate?.managers ?? [],
      }),
    },
    queryResult: {
      data: {
        ...indicationData!,
        persona: OrderBookPersonaType.SYNDICATE,
      },
      loading,
      error: error ?? null,
      stopPolling,
    },
  };
};
